import { datadogLogs } from "@datadog/browser-logs";
import { getKioskIdentity } from "../../lib/kiosk-identity";
import type { KioskMachineContext } from "../../machine/types";

/**
 * Loud fail-safe telemetry for a misconfigured kiosk.
 *
 * Fires as the XState entry action the instant the machine dead-ends at
 * `notConfigured` — i.e. a required launch identifier (practiceId and/or
 * ehrLocationId) was missing at machine construction, so the `welcome` state's
 * fail-safe routed here. Without this, a misconfigured tablet silently shows
 * the front-desk block screen and there is no prod signal that it happened.
 *
 * Emitted at WARN level so a misconfigured tablet does not re-trigger the
 * broad "New Error Log on SAM Prod" monitor (status:error) on every launch. It
 * still carries a distinctive `action` / `errorName` facet, so a targeted
 * monitor on `@context.action:kiosk_not_configured` can alert on it. Logs only
 * config identifiers and presence booleans — no PII.
 *
 * The message is written to be actionable on its own: whoever reads the alert
 * should be able to tell WHICH physical tablet is broken and WHAT to do about
 * it without opening the code. `action` and `errorName` stay stable so existing
 * monitors keep matching.
 */

/** Identify the physical device in a way that survives an alert being read cold. */
function buildKioskLabel(
  kioskId: string,
  kioskName: string | null,
): { kioskLabel: string; shortKioskId: string } {
  // The short id is the only reliably UNIQUE part: kioskName defaults to
  // "Medrite Kiosk" on every device that launched without ?kioskName=, so the
  // name alone cannot tell two tablets apart. Always include the short id.
  const shortKioskId = kioskId.slice(0, 8);
  const kioskLabel = kioskName
    ? `"${kioskName}" (#${shortKioskId})`
    : `#${shortKioskId}`;
  return { kioskLabel, shortKioskId };
}

/** What the person reading the alert should actually go do. */
function buildRemediation(
  hasPracticeId: boolean,
  practiceId: string | undefined,
): string {
  if (!hasPracticeId) {
    return "Relaunch this kiosk with the practiceId query param (e.g. https://kiosk.superscript.nyc/?practiceId=637). It is cached in localStorage on first launch, so this only needs doing once per device.";
  }
  return `Practice ${practiceId} has no ehrLocationId on file. The kiosk derives its EHR location from the practice record, so fix that record in config-manager — a query param will not override the derived value.`;
}

export function logNotConfiguredAction({
  context,
}: {
  context: KioskMachineContext;
}): void {
  const hasPracticeId =
    typeof context.practiceId === "string" && context.practiceId.length > 0;
  const hasEhrLocationId =
    typeof context.ehrLocationId === "string" &&
    context.ehrLocationId.length > 0;

  const missingIdentifiers: string[] = [];
  if (!hasPracticeId) missingIdentifiers.push("practiceId");
  if (!hasEhrLocationId) missingIdentifiers.push("ehrLocationId");

  const { kioskId, kioskName, kioskLocation } = getKioskIdentity();
  const { kioskLabel, shortKioskId } = buildKioskLabel(kioskId, kioskName);

  // Whatever the tablet actually launched with — the single most useful field
  // when reconstructing why config was missing. Kiosk query params are config
  // ids (practiceId / stateId / terminalId), never PII.
  const launchUrl =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : null;

  const remediation = buildRemediation(
    hasPracticeId,
    context.practiceId as string | undefined,
  );
  const locationSuffix = kioskLocation ? ` at ${kioskLocation}` : "";

  datadogLogs.logger.warn(
    `Kiosk ${kioskLabel}${locationSuffix} is missing ${missingIdentifiers.join(
      " and ",
    )} — check-in is BLOCKED at the front-desk screen. ${remediation}`,
    {
      context: {
        action: "kiosk_not_configured",
        errorName: "KioskNotConfigured",
        metadata: {
          missingIdentifiers,
          hasPracticeId,
          hasEhrLocationId,
          // Presence is the alertable signal; the resolved values (non-PII
          // config ids) help pinpoint which launch URL / cache entry is wrong.
          practiceId: hasPracticeId ? context.practiceId : null,
          ehrLocationId: hasEhrLocationId ? context.ehrLocationId : null,
          orgId: context.orgId ?? null,
          kioskId,
          shortKioskId,
          kioskName: kioskName ?? null,
          // Physical siting of the tablet, when it has been set — the "which
          // clinic do we walk to" field.
          kioskLocation: kioskLocation ?? null,
          launchUrl,
          remediation,
        },
      },
    },
  );
}
